import { ChevronRight } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Item, ItemContent, ItemHeader } from "@/components/ui/item"

import { JsonBlock } from "./json"
import { Markdown } from "./markdown"

export type DisplayPart =
  | { type: "text"; text: string }
  | { type: "reasoning"; text: string }
  | { type: "image"; src: string }
  | { type: "tool-call"; toolCallId: string; toolName: string; input: unknown }
  | { type: "tool-result"; toolCallId: string; output: unknown }
  | { type: "json"; value: unknown }

export type DisplayMessage = {
  role: "system" | "user" | "assistant" | "tool"
  content: DisplayPart[]
}

export function extractMessages(
  request: unknown,
  response: unknown,
  api: string,
): DisplayMessage[] | null {
  const req = request as any
  const res = response as any
  if (api === "/v1/messages") {
    return fromAnthropic(req, res)
  }
  if (api === "/responses") {
    return fromResponses(req, res)
  }
  if (api === "/chat/completions") {
    return fromChat(req, res)
  }
  return null
}

function asJson(v: unknown): unknown {
  if (typeof v !== "string") {
    return v
  }
  try {
    return JSON.parse(v)
  } catch {
    return v
  }
}

function dataUrl(s: any): string | null {
  if (s?.type === "base64" && typeof s?.data === "string") {
    return `data:${s.media_type ?? "image/png"};base64,${s.data}`
  }
  return null
}

function imageSrc(p: any): string | null {
  return (
    dataUrl(p?.source) ??
    (typeof p?.image_url === "string" ? p.image_url : null) ??
    (typeof p?.image_url?.url === "string" ? p.image_url.url : null) ??
    (typeof p?.url === "string" ? p.url : null)
  )
}

function asRole(role: string): DisplayMessage["role"] {
  if (role === "assistant") {
    return "assistant"
  }
  if (role === "system" || role === "developer") {
    return "system"
  }
  return "user"
}

function toolResult(id: unknown, output: unknown): DisplayPart {
  const text =
    typeof output === "string"
      ? output
      : Array.isArray(output) && output.every((b: any) => typeof b?.text === "string")
        ? output.map((b: any) => b.text).join("\n")
        : asJson(output)
  return {
    type: "tool-result",
    toolCallId: typeof id === "string" ? id : "",
    output: text,
  }
}

function part(p: any): DisplayPart {
  if (p == null) {
    return { type: "text", text: "" }
  }
  if (typeof p === "string") {
    return { type: "text", text: p }
  }
  switch (p.type) {
    case "text":
    case "input_text":
    case "output_text":
      return { type: "text", text: p.text ?? "" }
    case "input_image":
    case "image_url":
    case "image": {
      const src = imageSrc(p)
      return src ? { type: "image", src } : { type: "json", value: p }
    }
    case "tool_use":
    case "function_call":
      return {
        type: "tool-call",
        toolCallId: p.call_id ?? p.id ?? "",
        toolName: p.name ?? "",
        input: asJson(p.input ?? p.arguments),
      }
    case "tool_result":
    case "function_call_output":
      return toolResult(p.tool_use_id ?? p.call_id, p.output ?? p.content)
    case "reasoning": {
      const text =
        (p.summary ?? []).map((s: any) => s?.text ?? "").join("\n") ||
        p.reasoning_content ||
        p.thinking ||
        ""
      return text ? { type: "reasoning", text } : { type: "json", value: p }
    }
    default:
      return { type: "json", value: p }
  }
}

function textParts(c: any): DisplayPart[] | null {
  if (typeof c === "string") {
    return [{ type: "text", text: c }]
  }
  if (Array.isArray(c)) {
    return c.map(part)
  }
  return null
}

function fromAnthropic(req: any, res: any): DisplayMessage[] | null {
  if (!Array.isArray(req?.messages)) {
    return null
  }
  const messages: DisplayMessage[] = []
  if (req.system != null) {
    const parts = textParts(req.system)
    if (!parts) {
      return null
    }
    messages.push({ role: "system", content: parts })
  }
  for (const m of req.messages) {
    const parts = textParts(m?.content)
    if (!parts) {
      return null
    }
    messages.push({ role: m.role === "assistant" ? "assistant" : "user", content: parts })
  }
  if (Array.isArray(res?.content)) {
    messages.push({ role: "assistant", content: res.content.map(part) })
  }
  return messages
}

function fromResponses(req: any, res: any): DisplayMessage[] | null {
  if (typeof req?.input !== "string" && !Array.isArray(req?.input) && req?.input != null) {
    return null
  }
  const messages: DisplayMessage[] = []
  if (typeof req.instructions === "string") {
    messages.push({ role: "system", content: [{ type: "text", text: req.instructions }] })
  }
  const input = req?.input
  if (typeof input === "string") {
    messages.push({ role: "user", content: [{ type: "text", text: input }] })
  } else if (Array.isArray(input)) {
    for (const item of input) {
      if (item?.type === "reasoning") {
        continue
      }
      if (item?.type === "function_call") {
        messages.push({ role: "assistant", content: [part(item)] })
      } else if (item?.type === "function_call_output") {
        messages.push({ role: "tool", content: [part(item)] })
      } else if (item?.type === "message" || typeof item?.role === "string") {
        const parts = textParts(item.content)
        if (!parts) {
          return null
        }
        messages.push({ role: asRole(item.role), content: parts })
      } else {
        return null
      }
    }
  }
  if (Array.isArray(res?.output)) {
    const parts: DisplayPart[] = []
    for (const o of res.output) {
      if (o?.type === "message") {
        for (const p of Array.isArray(o.content) ? o.content : [o.content]) {
          parts.push(part(p))
        }
      } else {
        parts.push(part(o))
      }
    }
    messages.push({ role: "assistant", content: parts })
  }
  return messages
}

function chatAssistant(m: any): DisplayPart[] {
  const parts: DisplayPart[] = []
  if (typeof m?.reasoning_content === "string" && m.reasoning_content) {
    parts.push({ type: "reasoning", text: m.reasoning_content })
  }
  if (typeof m?.content === "string") {
    if (m.content) {
      parts.push({ type: "text", text: m.content })
    }
  } else if (Array.isArray(m?.content)) {
    parts.push(...m.content.map(part))
  }
  for (const tc of m?.tool_calls ?? []) {
    parts.push({
      type: "tool-call",
      toolCallId: tc?.id ?? "",
      toolName: tc?.function?.name ?? "",
      input: asJson(tc?.function?.arguments),
    })
  }
  return parts
}

function fromChat(req: any, res: any): DisplayMessage[] | null {
  if (!Array.isArray(req?.messages)) {
    return null
  }
  const messages: DisplayMessage[] = []
  for (const m of req.messages) {
    if (m.role === "tool") {
      messages.push({ role: "tool", content: [toolResult(m.tool_call_id, m.content)] })
    } else if (m.role === "assistant") {
      messages.push({ role: "assistant", content: chatAssistant(m) })
    } else if (m.role === "system" || m.role === "developer") {
      const parts = textParts(m.content)
      if (!parts) {
        return null
      }
      messages.push({ role: "system", content: parts })
    } else if (m.role === "user") {
      const parts = textParts(m.content)
      if (!parts) {
        return null
      }
      messages.push({ role: "user", content: parts })
    } else {
      return null
    }
  }
  const choice = res?.choices?.[0]?.message
  if (choice) {
    messages.push({ role: "assistant", content: chatAssistant(choice) })
  }
  return messages
}

type ToolCall = { call: Extract<DisplayPart, { type: "tool-call" }>; result?: DisplayPart }

type RenderItem =
  | { kind: "message"; message: DisplayMessage }
  | { kind: "tools"; calls: ToolCall[] }

function groupMessages(messages: DisplayMessage[]): RenderItem[] {
  const results = new Map<string, DisplayPart>()
  const callIds = new Set<string>()
  for (const m of messages) {
    for (const p of m.content) {
      if (p.type === "tool-result" && !results.has(p.toolCallId)) {
        results.set(p.toolCallId, p)
      }
      if (p.type === "tool-call") {
        callIds.add(p.toolCallId)
      }
    }
  }

  const items: RenderItem[] = []
  let buffer: ToolCall[] = []
  const flush = () => {
    if (buffer.length) {
      items.push({ kind: "tools", calls: buffer })
      buffer = []
    }
  }
  for (const m of messages) {
    const calls = m.content.filter((p): p is ToolCall["call"] => p.type === "tool-call")
    const rest = m.content.filter(
      (p) => p.type !== "tool-call" && !(p.type === "tool-result" && callIds.has(p.toolCallId)),
    )
    const hasResults = m.content.some((p) => p.type === "tool-result")
    if (rest.length || hasResults) {
      flush()
    }
    if (rest.length) {
      items.push({ kind: "message", message: { ...m, content: rest } })
    }
    buffer.push(...calls.map((call) => ({ call, result: results.get(call.toolCallId) })))
  }
  flush()
  return items
}

export function MessageList({ messages }: { messages: DisplayMessage[] }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
      {groupMessages(messages).map((item, i) =>
        item.kind === "message" ? (
          <MessageCard key={i} message={item.message} />
        ) : (
          <ToolGroup key={i} calls={item.calls} />
        ),
      )}
    </div>
  )
}

function MessageCard({ message: m }: { message: DisplayMessage }) {
  return (
    <Item variant="outline" size="sm" className="text-xs">
      <ItemHeader className="min-w-0">
        <Badge
          variant={m.role === "user" ? "default" : m.role === "assistant" ? "secondary" : "outline"}
        >
          {m.role}
        </Badge>
        {m.role === "tool" && m.content[0]?.type === "tool-result" && (
          <span className="min-w-0 flex-1 truncate font-mono text-muted-foreground">
            {m.content[0].toolCallId}
          </span>
        )}
      </ItemHeader>
      <ItemContent className="min-w-0">
        {m.content.length === 0 ? (
          <span className="text-muted-foreground">(empty)</span>
        ) : (
          m.content.map((p, j) => <PartView key={j} part={p} />)
        )}
      </ItemContent>
    </Item>
  )
}

function inlineInput(call: ToolCall["call"]): string {
  if (call.toolName === "bash" && typeof command(call.input) === "string") {
    return command(call.input) as string
  }
  return JSON.stringify(call.input)
}

function command(input: unknown): unknown {
  if (input && typeof input === "object" && "command" in input) {
    return (input as { command: unknown }).command
  }
  return undefined
}

function ToolGroup({ calls }: { calls: ToolCall[] }) {
  if (calls.length === 1) {
    return <ToolCallRow {...calls[0]} />
  }
  return (
    <div className="flex flex-col gap-2 border-l-2 pl-2">
      {calls.map((c, i) => (
        <ToolCallRow key={i} {...c} />
      ))}
    </div>
  )
}

function ToolCallRow({ call, result }: ToolCall) {
  return (
    <Collapsible className="group/call min-w-0">
      <CollapsibleTrigger className="flex w-full cursor-pointer items-center text-left">
        <Badge variant="outline" className="font-mono mr-1">
          {call.toolName}
        </Badge>
        <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground">
          {inlineInput(call)}
        </span>
        {!result && <span className="text-xs italic shrink-0 text-muted-foreground">pending</span>}
        <ChevronRight className="size-3.5 shrink-0 text-muted-foreground transition-transform group-data-open/call:rotate-90" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="flex flex-col gap-2 pt-2">
          <JsonBlock className="rounded-md bg-muted p-2" label="input" value={call.input} />
          {result?.type === "tool-result" && (
            <JsonBlock className="rounded-md bg-muted p-2" label="output" value={result.output} />
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

function PartView({ part }: { part: DisplayPart }) {
  switch (part.type) {
    case "text":
      return <Markdown text={part.text} />
    case "reasoning":
      return <div className="whitespace-pre-wrap italic text-muted-foreground">{part.text}</div>
    case "image":
      return (
        <img
          src={part.src}
          alt=""
          className="max-h-40 self-start rounded border"
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
      )
    case "tool-result":
      return <JsonBlock className="rounded-md bg-muted p-2" label="output" value={part.output} />
    case "json":
      return <JsonBlock className="rounded-md bg-muted p-2" label="json" value={part.value} />
  }
}
